/**
 * @file Product Carousel Component
 * @description Web Component for Splide-based product image carousel with thumbnail navigation
 *
 * Features:
 * - Main carousel with lazy loading
 * - Synced thumbnail navigation
 * - Responsive breakpoints
 * - Mobile arrows/pagination toggle via data attribute
 * - Variant-based image navigation
 * - Fade-in effect on lazy load
 * - Reduced motion support
 *
 * Usage:
 * <product-carousel data-use-mobile-arrows="true">
 *   <div id="pdp-main-carousel" class="splide">...</div>
 *   <div id="pdp-main-thumbnails" class="splide">...</div>
 * </product-carousel>
 */

class ProductCarousel extends HTMLElement {
  constructor() {
    super();
    this.mainCarousel = null;
    this.thumbnailCarousel = null;
    this.useMobileArrows = false;
  }

  connectedCallback() {
    // Wait for Splide library to be available
    if (typeof Splide === 'undefined') {
      console.error('ProductCarousel: Splide library not loaded');
      return;
    }

    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  /**
   * Initialize carousel
   */
  init() {
    // Check if mobile arrows/pagination should be used
    this.useMobileArrows = this.dataset.useMobileArrows === 'true';

    // Get carousel elements
    const mainElement = this.querySelector('#pdp-main-carousel');
    const thumbnailElement = this.querySelector('#pdp-main-thumbnails');

    if (!mainElement) {
      console.warn('ProductCarousel: Main carousel element not found');
      return;
    }

    // Initialize main carousel
    this.mainCarousel = new Splide(mainElement, {
      type: 'slide',
      rewind: false,
      pagination: false,
      arrows: false,
      gap: 16,
      lazyLoad: 'nearby',
      preloadPages: 1,
      speed: 400,
      reducedMotion: {
        speed: 0,
        rewindSpeed: 0,
      },
      breakpoints: {
        768: {
          pagination: this.useMobileArrows,
          arrows: this.useMobileArrows,
        }
      }
    });

    // Initialize thumbnail carousel if it exists
    if (thumbnailElement) {
      this.thumbnailCarousel = new Splide(thumbnailElement, {
        fixedWidth: 60,
        fixedHeight: 60,
        gap: 10,
        rewind: false,
        pagination: false,
        arrowPath: 'm21.9531 20.0008-8.25-8.25 2.3567-2.35671L26.6665 20.0008 16.0598 30.6074l-2.3567-2.3566 8.25-8.25Z',
        isNavigation: true,
        breakpoints: {
          900: {
            perPage: 3,
          },
          600: {
            fixedWidth: 48,
            fixedHeight: 48,
            perPage: 2,
            gap: 8
          }
        }
      });

      // Sync carousels
      this.mainCarousel.sync(this.thumbnailCarousel);
    }

    // Mount carousels
    this.mainCarousel.mount();
    if (this.thumbnailCarousel) {
      this.thumbnailCarousel.mount();
    }

    // Add fade-in effect when lazy images load
    this.mainCarousel.on('lazyload:loaded', (img) => {
      img.classList.add('is-loaded');
    });

    // Setup variant-based slide navigation
    this.setupVariantNavigation();
  }

  /**
   * Setup variant-change listener for carousel navigation
   */
  setupVariantNavigation() {
    const variantSelector = document.querySelector('variant-selector');
    if (!variantSelector) return;

    variantSelector.addEventListener('variant-change', (e) => {
      const variant = e.detail.variant;
      if (!variant) return;

      // Try featured_media first (Liquid variant object), then featured_image (JSON variant object)
      const mediaId = variant.featured_media?.id || variant.featured_image?.id;
      
      if (mediaId) {
        this.navigateToVariantImage(mediaId);
      }
    });
  }

  /**
   * Navigate carousel to image matching variant
   * @param {number} mediaId - Media ID to navigate to
   */
  navigateToVariantImage(mediaId) {
    if (!this.mainCarousel) return;

    const slides = this.mainCarousel.Components.Slides.get();
    const targetIndex = slides.findIndex(slide => {
      const img = slide.slide.querySelector('img[data-image-id]');
      return img && parseInt(img.dataset.imageId) === mediaId;
    });

    if (targetIndex !== -1) {
      this.mainCarousel.go(targetIndex);
    }
  }

  /**
   * Get main carousel instance
   * @returns {Object|null} Splide instance
   */
  getMainCarousel() {
    return this.mainCarousel;
  }

  /**
   * Get thumbnail carousel instance
   * @returns {Object|null} Splide instance
   */
  getThumbnailCarousel() {
    return this.thumbnailCarousel;
  }

  /**
   * Cleanup carousels
   */
  cleanup() {
    if (this.mainCarousel) {
      this.mainCarousel.destroy();
      this.mainCarousel = null;
    }

    if (this.thumbnailCarousel) {
      this.thumbnailCarousel.destroy();
      this.thumbnailCarousel = null;
    }
  }
}

if (!customElements.get('product-carousel')) {
  customElements.define('product-carousel', ProductCarousel);
}

export default ProductCarousel;
